import { useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  EmailAuthProvider,
  linkWithCredential,
  linkWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: unauthenticated, 2: profile incomplete, 3: authenticated
  const auth = getAuth();

  useEffect(() => {
    let userDocUnsubscribe = null; // To hold the onSnapshot unsubscribe function

    const authUnsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (userDocUnsubscribe) {
        userDocUnsubscribe(); // Unsubscribe from previous user's doc
      }

      if (firebaseUser) {
        setLoading(true);
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        
        userDocUnsubscribe = onSnapshot(userDocRef, (userDoc) => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fullUser = { ...firebaseUser, ...userData };
            setUser(fullUser);
            // Check if verified OR legacy user (has name/class)
            if (userData.isVerified || (userData.name && userData.class)) {
              setStep(3); // Authenticated and profile complete
            } else {
              setStep(2); // Profile incomplete
            }
          } else {
            // New user, create doc in Firestore, then onSnapshot will re-trigger
            const initialData = {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              createdAt: new Date(),
              totalStudyTime: 0,
              isVerified: false,
              tutorial_completed: false, // チュートリアルフラグを追加
            };
            setDoc(userDocRef, initialData).then(() => {
                 // The onSnapshot listener will handle the user state update
            }).catch(error => {
                console.error("Error creating user document:", error);
            });
          }
          setLoading(false);
        }, (error) => {
          console.error("Error with onSnapshot listener:", error);
          setUser(null);
          setStep(1);
          setLoading(false);
        });

      } else {
        // User is signed out
        if (userDocUnsubscribe) {
          userDocUnsubscribe();
        }
        setUser(null);
        setStep(1);
        setLoading(false);
      }
    });

    // Cleanup on component unmount
    return () => {
      authUnsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
    };
  }, [auth]);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google: ", error);
      throw error;
    }
  };

  const signUpWithEmail = async (email, password) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error signing up with email: ", error);
      throw error;
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error signing in with email: ", error);
      throw error;
    }
  };

  const handleSignInAnonymously = async () => {
    try {
      await signInAnonymously(auth); // 正しい呼び出し方
    } catch (error) {
      console.error("Error signing in anonymously: ", error);
      throw error;
    }
  };

  const linkEmailPassword = async (email, password) => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }

    try {
      const credential = EmailAuthProvider.credential(email, password);
      await linkWithCredential(auth.currentUser, credential);
    } catch (error) {
      console.error("Error linking email and password:", error);
      throw error; // Re-throw to be handled by the caller
    }
  };

  const linkWithGoogle = async () => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }

    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(auth.currentUser, provider);
    } catch (error) {
      console.error("Error linking with Google:", error);
      throw error; // Re-throw to be handled by the caller
    }
  };

  const logOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };
  
  const updateUserContext = (userData) => {
    setUser(currentUser => ({...currentUser, ...userData}));
  };

  return { user, loading, step, setStep, signInWithGoogle, signUpWithEmail, signInWithEmail, handleSignInAnonymously, logOut, updateUserContext, linkEmailPassword, linkWithGoogle };
};